/**
 * InsightCardMultiPageRenderer
 *
 * Renders a multi-page report with sidebar navigation and filtering.
 * Page groups are displayed as accordion entries in the sidebar.
 *
 * Layout config drives the sidebar and filter behavior:
 * - sidebar.group.property.id: which property to group pages by
 * - top_filter.properties: which properties to use as filters
 *
 * Multi-group support:
 * - Page groups are rendered as expandable accordion entries (multiple can be open)
 * - Each group with an explorer layout gets a filter icon that opens a scoped FilterModal
 * - Filters are scoped per group (persisted independently in localStorage)
 * - Active group is tracked via URL param (?group=<id>)
 * - Search works across all groups, auto-expanding matching ones
 *
 * Supports two block formats:
 * - InsightCardBlock: Layout-based with block_elements (renders with InsightCard wrapper)
 * - FreeFlowBlock: Direct UIBlock rendering (renders without wrapper, like chat)
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, LoadingSkeleton } from '@strivelabs/ui-blocks';
import {
  Button, Input, Checkbox, Avatar, AvatarImage, AvatarFallback, toast,
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '@strivelabs/ui-core';
import { Search, SlidersHorizontal, X, ChevronDown, ChevronRight, PanelLeftClose, PanelLeftOpen, User } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import type { PageGroup, Page, PageGroupLayout, UIActionEvent, PageRef } from '@/types/ui-blocks';
import { ReportBlocksRenderer } from './report-blocks-renderer';
import { ReportNavigationProvider } from '@/contexts/ReportNavigationContext';
import { getGreeting } from '@/utils/timeUtils';
import Fuse from 'fuse.js';

export interface InsightCardMultiPageRendererProps {
  /** Array of page groups to render as accordion entries in the sidebar. */
  pageGroups: PageGroup[];
  /** Callback fired when user interacts with UI components */
  onUIAction?: (event: UIActionEvent) => void;
  /** Additional CSS classes for the container */
  className?: string;
}

/**
 * Group pages by a specific property ID
 */
const groupPagesByPropertyId = (pages: Page[], propertyId: string): Record<string, Page[]> => {
  const groups: Record<string, Page[]> = {};

  pages.forEach(page => {
    const property = page.properties?.find(p => p.id === propertyId);
    const groupKey = property ? String(property.value) : 'Other';

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(page);
  });

  return groups;
};

/**
 * Extract unique filter options from all pages based on layout config.
 * Uses top_filter property IDs from the explorer layout to determine which properties are filters.
 */
const extractFilterOptions = (
  pages: Page[],
  layout: PageGroupLayout | undefined,
  propertyDefs: PageGroup['properties']
): Array<{ name: string; value: string }> => {
  if (!layout) return [];

  const filterPropertyIds = layout.config.top_filter.properties.map(p => p.id);
  const filterSet = new Set<string>();
  const filters: Array<{ name: string; value: string }> = [];

  pages.forEach(page => {
    page.properties?.forEach(prop => {
      if (filterPropertyIds.includes(prop.id)) {
        const displayName = propertyDefs?.find(d => d.id === prop.id)?.display_name || prop.id;
        const valueStr = String(prop.value);
        const key = `${displayName}:${valueStr}`;
        if (!filterSet.has(key)) {
          filterSet.add(key);
          filters.push({ name: displayName, value: valueStr });
        }
      }
    });
  });

  return filters;
};

/**
 * Get the label property ID for a page group.
 * Returns the first property that's not the grouping or filter property.
 */
const getLabelPropertyId = (group: PageGroup): string | undefined => {
  const layout = group.layouts?.find(l => l.type === 'explorer');
  const propertyDefs = group.properties;
  if (!propertyDefs) return undefined;

  const groupingPropertyId = layout?.config.sidebar.group.property.id;
  const filterPropertyIds = layout?.config.top_filter.properties.map(p => p.id) || [];

  return propertyDefs.find(d =>
    d.id !== groupingPropertyId && !filterPropertyIds.includes(d.id)
  )?.id || propertyDefs[0]?.id;
};

/**
 * Apply filters to pages using AND logic across filter types, OR within each type.
 */
const applyFiltersToPages = (
  pages: Page[],
  selectedFilters: string[],
  layout: PageGroupLayout | undefined,
  propertyDefs: PageGroup['properties']
): Page[] => {
  if (selectedFilters.length === 0 || !layout) return pages;

  const filterOptions = extractFilterOptions(pages, layout, propertyDefs);
  const filterPropertyIds = layout.config.top_filter.properties.map(p => p.id);

  if (filterPropertyIds.length === 0) return pages;

  // Group selected filters by their property type
  const filtersByType = filterOptions.reduce((acc, filterOption) => {
    if (selectedFilters.includes(filterOption.value)) {
      if (!acc[filterOption.name]) {
        acc[filterOption.name] = [];
      }
      acc[filterOption.name].push(filterOption.value);
    }
    return acc;
  }, {} as Record<string, string[]>);

  // Filter pages: must match at least one value from EACH active filter type (AND logic)
  return pages.filter(page => {
    return Object.entries(filtersByType).every(([filterName, filterValues]) => {
      const propertyDef = propertyDefs?.find(d => d.display_name === filterName);
      if (!propertyDef) return true;
      const pageValue = page.properties?.find(p => p.id === propertyDef.id);
      if (!pageValue) return false;
      return filterValues.includes(String(pageValue.value));
    });
  });
};

/**
 * Apply fuzzy search to pages using Fuse.js
 */
const applySearchToPages = (pages: Page[], query: string): Page[] => {
  if (!query.trim()) return pages;

  const fuse = new Fuse(pages, {
    keys: ['properties'],
    threshold: 0.4,
    ignoreLocation: true,
    useExtendedSearch: false,
    getFn: (page) => {
      return (page as Page).properties
        ?.map(prop => String(prop.value))
        .join(' ') || '';
    },
  });

  return fuse.search(query).map(result => result.item);
};

/**
 * Filter modal component
 */
const FilterModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  filters: Array<{ name: string; value: string }>;
  selectedFilters: string[];
  onFiltersSaved: (filters: string[]) => void;
}> = ({ isOpen, onClose, filters, selectedFilters, onFiltersSaved }) => {
  const [localSelectedFilters, setLocalSelectedFilters] = useState<string[]>(selectedFilters);

  useEffect(() => {
    setLocalSelectedFilters(selectedFilters);
  }, [selectedFilters, isOpen]);

  if (!isOpen) return null;

  const handleToggleFilter = (filterValue: string) => {
    setLocalSelectedFilters(prev =>
      prev.includes(filterValue)
        ? prev.filter(f => f !== filterValue)
        : [...prev, filterValue]
    );
  };

  const handleClearAll = () => {
    setLocalSelectedFilters([]);
  };

  const handleSave = () => {
    onFiltersSaved(localSelectedFilters);
    onClose();
  };

  // Group filters by name
  const groupedFilters = filters.reduce((acc, filter) => {
    if (!acc[filter.name]) {
      acc[filter.name] = [];
    }
    acc[filter.name].push(filter.value);
    return acc;
  }, {} as Record<string, string[]>);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="border-b border-gray-200 p-4 mb-2 border-opacity-70">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-medium text-black">Filter Pages</h2>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                disabled={localSelectedFilters.length === 0}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Clear all
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose} className="p-1">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Select filters to show only matching pages.
          </p>
        </div>

        {/* Filter Groups */}
        <div className="space-y-6 p-4 max-h-[60vh] overflow-y-auto">
          {Object.entries(groupedFilters).map(([filterName, values]) => (
            <div key={filterName} className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">{filterName}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Filter pages by {filterName.toLowerCase()}
                  </p>
                </div>
                {localSelectedFilters.some(f => values.includes(f)) && (
                  <span className="text-xs text-primary-600 font-medium">
                    {values.filter(v => localSelectedFilters.includes(v)).length} selected
                  </span>
                )}
              </div>

              <div className="space-y-2 pl-2">
                {values.map(value => (
                  <div key={value} className="flex items-start space-x-3 py-1.5">
                    <Checkbox
                      id={`filter-${filterName}-${value}`}
                      checked={localSelectedFilters.includes(value)}
                      onCheckedChange={() => handleToggleFilter(value)}
                      className="h-4 w-4 mt-0.5"
                    />
                    <label
                      htmlFor={`filter-${filterName}-${value}`}
                      className="flex-1 cursor-pointer text-sm text-gray-900"
                    >
                      {value}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center gap-2 p-4 border-t border-gray-200 mt-2 border-opacity-70">
          <div className="text-xs text-gray-600">
            {localSelectedFilters.length > 0 ? (
              <span>{localSelectedFilters.length} filter{localSelectedFilters.length > 1 ? 's' : ''} selected</span>
            ) : (
              <span>No filters selected (showing all pages)</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={onClose} variant="outline" className="px-4 py-1.5 text-sm h-8">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="destructive"
              className="px-4 py-1.5 text-sm h-8 bg-black hover:bg-gray-800 text-white"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Collapsible group component for sidebar (property-level grouping within a page group)
 */
const PropertyGroup: React.FC<{
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}> = ({ title, children, defaultExpanded = true }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="space-y-1">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-2 py-2 hover:bg-gray-50 rounded-md transition-colors group"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          )}
          <span className="text-sm font-medium text-gray-700">{title}</span>
        </div>
      </button>
      {isExpanded && <div className="pl-6 space-y-1">{children}</div>}
    </div>
  );
};

export const InsightCardMultiPageRenderer: React.FC<InsightCardMultiPageRendererProps> = ({
  pageGroups,
  onUIAction = () => {},
  className,
}) => {
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);
  const previousHasResultsRef = useRef<boolean>(true);

  // --- Multi-group state ---
  // Per-group filters: Record<groupId, selectedFilterValues[]>
  const [filtersByGroup, setFiltersByGroup] = useState<Record<string, string[]>>({});
  // Which group's filter icon was clicked (null = modal closed)
  const [filterModalGroupId, setFilterModalGroupId] = useState<string | null>(null);
  // Accordion expanded groups (multiple can be open)
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  // Track pre-search expand state for restore
  const preSearchExpandedRef = useRef<string[] | null>(null);

  const greeting = getGreeting();
  const userName = user?.firstName || 'User';
  const userAvatar = user?.imageUrl;

  // Derive active page group from URL param (?group=<id>), defaults to first group
  const activeGroupId = useMemo(() => {
    const groupParam = searchParams.get('group');
    if (groupParam) {
      const found = pageGroups.find(g => g.id === groupParam);
      if (found) return found.id;
    }
    return pageGroups[0]?.id || '';
  }, [searchParams, pageGroups]);

  const activeGroup = useMemo(
    () => pageGroups.find(g => g.id === activeGroupId) || pageGroups[0],
    [pageGroups, activeGroupId]
  );

  // Get page index from URL (1-based in URL, 0-based internally)
  const selectedPageIndex = useMemo(() => {
    const pageParam = searchParams.get('page');
    if (pageParam) {
      const parsed = parseInt(pageParam, 10);
      if (!isNaN(parsed) && parsed >= 1) {
        return parsed - 1;
      }
    }
    return 0;
  }, [searchParams]);

  // Update URL when page is selected (handles both group and page params)
  const selectPage = useCallback((groupId: string, pageIndex: number) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (groupId === pageGroups[0]?.id) {
        newParams.delete('group');
      } else {
        newParams.set('group', groupId);
      }
      if (pageIndex === 0) {
        newParams.delete('page');
      } else {
        newParams.set('page', String(pageIndex + 1));
      }
      return newParams;
    }, { replace: true });

    // Ensure the group is expanded
    setExpandedGroups(prev =>
      prev.includes(groupId) ? prev : [...prev, groupId]
    );
  }, [setSearchParams, pageGroups]);

  // Navigate to a page by PageRef (resolves page_id to index, then delegates to selectPage)
  const navigateToPage = useCallback((ref: PageRef) => {
    const group = pageGroups.find(g => g.id === ref.page_group_id);
    if (!group) return;
    const pageIndex = group.pages.findIndex(p => p.id === ref.page_id);
    if (pageIndex === -1) return;
    selectPage(ref.page_group_id, pageIndex);
  }, [pageGroups, selectPage]);

  // Initialize expanded groups to include the active group
  useEffect(() => {
    if (activeGroupId && !expandedGroups.includes(activeGroupId) && !isLoadingFilters) {
      setExpandedGroups(prev =>
        prev.includes(activeGroupId) ? prev : [...prev, activeGroupId]
      );
    }
    // Only run on initial mount / activeGroupId change, not on every expandedGroups change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupId, isLoadingFilters]);

  // Load filters for all groups from localStorage on mount
  useEffect(() => {
    setIsLoadingFilters(true);
    try {
      const loaded: Record<string, string[]> = {};
      pageGroups.forEach(group => {
        const stored = localStorage.getItem(`insight-card-filters-${group.id}`);
        if (stored) {
          loaded[group.id] = JSON.parse(stored) as string[];
        }
      });
      setFiltersByGroup(loaded);
    } catch (error) {
      console.error('Failed to load filters from localStorage:', error);
      setFiltersByGroup({});
    } finally {
      setIsLoadingFilters(false);
    }
  }, [pageGroups]);

  // Save filters to localStorage whenever they change (after initial load)
  useEffect(() => {
    if (isLoadingFilters) return;
    try {
      Object.entries(filtersByGroup).forEach(([groupId, filters]) => {
        localStorage.setItem(`insight-card-filters-${groupId}`, JSON.stringify(filters));
      });
    } catch (error) {
      console.error('Failed to save filters to localStorage:', error);
    }
  }, [filtersByGroup, isLoadingFilters]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Derived search state — single source of truth for "is user actively searching?"
  const isSearchActive = debouncedSearchQuery.trim() !== '';

  // --- Step 1: Compute filter-only pages (group-scoped filters, no search) ---
  const filterOnlyPagesByGroup = useMemo(() => {
    const result: Record<string, Page[]> = {};
    pageGroups.forEach(group => {
      const layout = group.layouts?.find(l => l.type === 'explorer');
      const groupFilters = filtersByGroup[group.id] || [];
      result[group.id] = applyFiltersToPages(group.pages, groupFilters, layout, group.properties);
    });
    return result;
  }, [pageGroups, filtersByGroup]);

  // --- Step 2: Compute search-filtered pages (filters + search) ---
  const searchFilteredPagesByGroup = useMemo(() => {
    if (!isSearchActive) return filterOnlyPagesByGroup;
    const result: Record<string, Page[]> = {};
    Object.keys(filterOnlyPagesByGroup).forEach(groupId => {
      result[groupId] = applySearchToPages(filterOnlyPagesByGroup[groupId] || [], debouncedSearchQuery);
    });
    return result;
  }, [filterOnlyPagesByGroup, debouncedSearchQuery, isSearchActive]);

  // --- Global search status ---
  const hasGlobalSearchResults = useMemo(() => {
    if (!isSearchActive) return true;
    return Object.values(searchFilteredPagesByGroup).some(pages => pages.length > 0);
  }, [searchFilteredPagesByGroup, isSearchActive]);

  // Sidebar shows search-filtered pages when results exist, falls back to filter-only when no results globally
  const sidebarPagesByGroup = hasGlobalSearchResults
    ? searchFilteredPagesByGroup
    : filterOnlyPagesByGroup;

  // Auto-expand groups when search is active
  useEffect(() => {
    if (isSearchActive) {
      // Save pre-search state
      if (preSearchExpandedRef.current === null) {
        preSearchExpandedRef.current = expandedGroups;
      }
      // Expand groups with matching pages
      const matchingGroupIds = pageGroups
        .filter(g => (searchFilteredPagesByGroup[g.id]?.length || 0) > 0)
        .map(g => g.id);
      setExpandedGroups(matchingGroupIds);
    } else if (preSearchExpandedRef.current !== null) {
      // Restore pre-search state
      setExpandedGroups(preSearchExpandedRef.current);
      preSearchExpandedRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, pageGroups]);

  // --- Main content page resolution (uses filter-only, search never affects main content) ---
  const activeFilterOnlyPages = filterOnlyPagesByGroup[activeGroupId] || [];
  const activeGroupAllPages = activeGroup?.pages || [];
  const hasFilterNoResults = activeFilterOnlyPages.length === 0 &&
    (filtersByGroup[activeGroupId]?.length || 0) > 0;
  const activeDisplayPages = hasFilterNoResults ? activeGroupAllPages : activeFilterOnlyPages;

  // --- Toast: filter no-results (per active group) ---
  useEffect(() => {
    if (hasFilterNoResults && previousHasResultsRef.current) {
      toast({
        title: "No matches found",
        description: "Try adjusting your filter criteria.",
        variant: "destructive",
      });
    }
    previousHasResultsRef.current = !hasFilterNoResults;
  }, [hasFilterNoResults]);

  // --- Toast: search no-results (global) ---
  const previousSearchHasResultsRef = useRef<boolean>(true);
  useEffect(() => {
    const noResults = isSearchActive && !hasGlobalSearchResults;
    if (noResults && previousSearchHasResultsRef.current) {
      toast({
        title: "No matches found",
        description: "Try adjusting your search query.",
        variant: "destructive",
      });
    }
    previousSearchHasResultsRef.current = !noResults;
  }, [isSearchActive, hasGlobalSearchResults]);

  // Reset selected page if it's filtered out
  useEffect(() => {
    if (selectedPageIndex >= activeDisplayPages.length && activeDisplayPages.length > 0) {
      selectPage(activeGroupId, 0);
    }
  }, [activeDisplayPages.length, selectedPageIndex, selectPage, activeGroupId]);

  // Get the currently selected page
  const selectedPage = activeDisplayPages[selectedPageIndex];

  // Compute total page counts
  const totalFilteredPages = useMemo(() => {
    return Object.values(sidebarPagesByGroup).reduce((sum, pages) => sum + pages.length, 0);
  }, [sidebarPagesByGroup]);

  const totalPages = useMemo(() => {
    return pageGroups.reduce((sum, g) => sum + g.pages.length, 0);
  }, [pageGroups]);

  // --- FilterModal helpers ---
  const filterModalGroup = filterModalGroupId
    ? pageGroups.find(g => g.id === filterModalGroupId)
    : null;

  const filterModalLayout = filterModalGroup?.layouts?.find(l => l.type === 'explorer');

  const filterModalOptions = useMemo(() => {
    if (!filterModalGroup) return [];
    return extractFilterOptions(filterModalGroup.pages, filterModalLayout, filterModalGroup.properties);
  }, [filterModalGroup, filterModalLayout]);

  const filterModalSelectedFilters = filterModalGroupId
    ? (filtersByGroup[filterModalGroupId] || [])
    : [];

  const handleFiltersSaved = useCallback((filters: string[]) => {
    if (filterModalGroupId) {
      setFiltersByGroup(prev => ({ ...prev, [filterModalGroupId]: filters }));
    }
    setFilterModalGroupId(null);
  }, [filterModalGroupId]);

  const clearGroupFilters = useCallback((groupId: string) => {
    setFiltersByGroup(prev => ({ ...prev, [groupId]: [] }));
  }, []);

  // Shared function to render page blocks
  const renderPageBlocks = (page: Page | undefined) => {
    if (!page || !page.blocks || !Array.isArray(page.blocks) || page.blocks.length === 0) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-500">
          <p>No content available for this page.</p>
        </div>
      );
    }
    return (
      <ReportNavigationProvider navigateToPage={navigateToPage}>
        <ReportBlocksRenderer blocks={page.blocks} onUIAction={onUIAction} />
      </ReportNavigationProvider>
    );
  };

  // Render the page list for a specific group
  const renderGroupPages = (group: PageGroup) => {
    const layout = group.layouts?.find(l => l.type === 'explorer');
    const groupingPropertyId = layout?.config.sidebar.group.property.id;
    const labelPropertyId = getLabelPropertyId(group);

    // Sidebar display pages (filtered by both filters + search)
    const displayPages = sidebarPagesByGroup[group.id] || [];
    // Index resolution uses filter-only pages for stable URL indices
    const indexPages = filterOnlyPagesByGroup[group.id] || [];

    if (displayPages.length === 0) {
      return (
        <div className="px-2 py-3 text-xs text-gray-500 italic">
          No pages available.
        </div>
      );
    }

    // Pre-compute page→index map for O(1) lookups instead of O(n) indexOf per button
    const pageIndexMap = new Map<Page, number>();
    indexPages.forEach((page, i) => pageIndexMap.set(page, i));

    const renderPageButton = (page: Page, fallbackIndex: number) => {
      // Resolve stable index from filter-only pages for URL consistency
      const stableIndex = pageIndexMap.get(page) ?? -1;
      const pageIndex = stableIndex >= 0 ? stableIndex : fallbackIndex;
      const labelProp = labelPropertyId
        ? page.properties?.find(p => p.id === labelPropertyId)
        : page.properties?.[0];
      const label = labelProp ? String(labelProp.value) : `Page ${pageIndex + 1}`;
      const isActive = group.id === activeGroupId && pageIndex === selectedPageIndex;

      return (
        <button
          key={page.id || pageIndex}
          onClick={() => selectPage(group.id, pageIndex)}
          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
            isActive
              ? 'bg-gray-100 font-medium text-gray-900'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          {label}
        </button>
      );
    };

    if (groupingPropertyId) {
      const grouped = groupPagesByPropertyId(displayPages, groupingPropertyId);
      return (
        <div className="space-y-2">
          {Object.entries(grouped).map(([groupName, pages]) => (
            <PropertyGroup key={groupName} title={groupName}>
              {pages.map((page, i) => renderPageButton(page, i))}
            </PropertyGroup>
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-1">
        {displayPages.map((page, pageIndex) => renderPageButton(page, pageIndex))}
      </div>
    );
  };

  // Show loading state while filters are being loaded
  if (isLoadingFilters) {
    return (
      <Container padding="lg" columns={1} className={className}>
        <LoadingSkeleton />
      </Container>
    );
  }

  // Handle case where there are no page groups
  if (!pageGroups || pageGroups.length === 0) {
    return (
      <Container padding="lg" columns={1} className={className}>
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <p className="text-lg font-medium mb-2">No Pages Available</p>
            <p className="text-sm">This insight report has no data to display.</p>
          </div>
        </div>
      </Container>
    );
  }

  // Derived values for single page group UI
  const isSingleGroup = pageGroups.length === 1;
  const singleGroup = isSingleGroup ? pageGroups[0] : null;
  const singleGroupLayout = singleGroup?.layouts?.find(l => l.type === 'explorer') ?? null;
  const singleGroupFilters = singleGroup ? (filtersByGroup[singleGroup.id] || []) : [];

  // Render collapsed sidebar
  if (sidebarCollapsed) {
    return (
      <div className="flex h-full">
        <div className="h-screen px-2 py-4 flex items-start flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            aria-label="Expand sidebar"
            className="w-8 h-8"
            onClick={() => setSidebarCollapsed(false)}
          >
            <PanelLeftOpen className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <Container padding="lg" columns={1} className={className}>
            {renderPageBlocks(selectedPage)}
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-4 h-full">
      {/* Sidebar */}
      <div className="col-span-3 flex flex-col h-screen overflow-hidden relative">
        {/* Collapse button */}
        <div className="absolute top-2 right-2 z-10">
          <Button
            variant="ghost"
            size="sm"
            aria-label="Collapse sidebar"
            className="w-8 h-8"
            onClick={() => setSidebarCollapsed(true)}
          >
            <PanelLeftClose className="h-4 w-4" />
          </Button>
        </div>

        <div className="px-6 py-8 flex flex-col h-full overflow-hidden">
          {/* User Section - Fixed */}
          <div className="space-y-4 px-2 flex-shrink-0 mb-8">
            {/* Avatar */}
            <div className="flex justify-start">
              {userAvatar ? (
                <Avatar className="rounded-lg w-avatar h-avatar">
                  <AvatarImage
                    src={userAvatar}
                    alt={userName}
                    className="object-cover"
                  />
                  <AvatarFallback className="text-sm">
                    {userName.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <div className="w-12 h-12 rounded bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
              )}
            </div>

            {/* Greeting */}
            <div className="text-left">
              <p className="text-sm text-custom-text-tertiary mb-1">{greeting}</p>
              <h2 className="text-base font-semibold text-custom-text-primary">
                {userName}
              </h2>
            </div>
          </div>

          {/* Search + Filter (inline for single group) */}
          <div className="flex-shrink-0 mb-4 px-2">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  type="text"
                  placeholder="Search pages..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-9 h-9 text-sm bg-custom-card-bg border-custom-border-primary"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {/* Filter button inline with search for single page group */}
              {singleGroup && singleGroupLayout && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0 flex-shrink-0 relative border border-custom-border-primary"
                  onClick={() => setFilterModalGroupId(singleGroup.id)}
                  aria-label={`Filter ${singleGroup.display_name || singleGroup.id}`}
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  {singleGroupFilters.length > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 min-w-4 px-0.5 rounded-full bg-gray-900 text-white text-[10px] font-medium flex items-center justify-center">
                      {singleGroupFilters.length}
                    </span>
                  )}
                </Button>
              )}
            </div>
            {/* Active filters indicator for single page group */}
            {singleGroup && singleGroupFilters.length > 0 && (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-gray-600">
                  {singleGroupFilters.length} filter{singleGroupFilters.length > 1 ? 's' : ''} active
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => clearGroupFilters(singleGroup.id)}
                  className="h-5 text-xs px-1.5"
                >
                  Clear
                </Button>
              </div>
            )}
          </div>

          {/* Page Groups - Scrollable */}
          <div className="flex-1 overflow-y-auto px-2">
            {singleGroup ? (
              // Single page group: skip group-level grouping, render pages directly
              renderGroupPages(singleGroup)
            ) : (
              <Accordion
                type="multiple"
                value={expandedGroups}
                onValueChange={setExpandedGroups}
              >
                {pageGroups.map(group => {
                  const groupLayout = group.layouts?.find(l => l.type === 'explorer');
                  const hasFilters = !!groupLayout;
                  const groupFilters = filtersByGroup[group.id] || [];
                  const sidebarCount = sidebarPagesByGroup[group.id]?.length || 0;
                  const isSinglePage = group.pages.length <= 1;

                  // Hide groups with no search results (only when search has global results elsewhere)
                  const groupHasSearchResults = (searchFilteredPagesByGroup[group.id]?.length || 0) > 0;
                  if (isSearchActive && hasGlobalSearchResults && !groupHasSearchResults) return null;

                  // Single-page groups render as a flat clickable item (no accordion)
                  if (isSinglePage) {
                    const isActive = group.id === activeGroupId && selectedPageIndex === 0;
                    return (
                      <div key={group.id} className="border-b border-gray-100 last:border-b-0">
                        <button
                          onClick={() => selectPage(group.id, 0)}
                          className={`w-full text-left py-3 px-2 rounded-md text-sm font-medium transition-colors ${
                            isActive
                              ? 'bg-gray-100 text-custom-text-primary'
                              : 'text-custom-text-primary hover:bg-gray-50'
                          }`}
                        >
                          {group.display_name || group.id}
                        </button>
                      </div>
                    );
                  }

                  return (
                    <AccordionItem
                      key={group.id}
                      value={group.id}
                      className="border-b border-gray-100 last:border-b-0"
                    >
                      <AccordionTrigger className="py-3 hover:no-underline hover:bg-gray-50 rounded-md px-2 [&[data-state=open]>svg]:rotate-180">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="text-sm font-medium text-custom-text-primary truncate">
                            {group.display_name || group.id}
                          </span>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {hasFilters && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 relative"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFilterModalGroupId(group.id);
                                }}
                                aria-label={`Filter ${group.display_name || group.id}`}
                              >
                                <SlidersHorizontal className="h-3.5 w-3.5" />
                                {groupFilters.length > 0 && (
                                  <span className="absolute -top-1 -right-1 h-4 min-w-4 px-0.5 rounded-full bg-gray-900 text-white text-[10px] font-medium flex items-center justify-center">
                                    {groupFilters.length}
                                  </span>
                                )}
                              </Button>
                            )}
                            <span className="text-xs text-gray-400">
                              ({sidebarCount})
                            </span>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pb-2 pt-0">
                        {/* Active filters indicator */}
                        {groupFilters.length > 0 && (
                          <div className="flex items-center gap-2 px-2 pb-2">
                            <span className="text-xs text-gray-600">
                              {groupFilters.length} filter{groupFilters.length > 1 ? 's' : ''} active
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => clearGroupFilters(group.id)}
                              className="h-5 text-xs px-1.5"
                            >
                              Clear
                            </Button>
                          </div>
                        )}
                        {/* Page list */}
                        {renderGroupPages(group)}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </div>

          {/* Results Count - Fixed at bottom */}
          <div className="flex-shrink-0 px-2 pt-4 border-t mt-4">
            <span className="text-xs text-gray-500">
              {isSearchActive || Object.values(filtersByGroup).some(f => f.length > 0) ? (
                <>{totalFilteredPages} of {totalPages} page{totalPages > 1 ? 's' : ''}</>
              ) : (
                <>{totalPages} page{totalPages > 1 ? 's' : ''}</>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="col-span-9 overflow-y-auto h-screen">
        <Container padding="lg" columns={1} className={className}>
          {renderPageBlocks(selectedPage)}
        </Container>
      </div>

      {/* Filter Modal - scoped to the group whose filter icon was clicked */}
      <FilterModal
        isOpen={filterModalGroupId !== null}
        onClose={() => setFilterModalGroupId(null)}
        filters={filterModalOptions}
        selectedFilters={filterModalSelectedFilters}
        onFiltersSaved={handleFiltersSaved}
      />
    </div>
  );
};

export default InsightCardMultiPageRenderer;
